import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Form, Button, Alert, Badge, Tab, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faUsers,
  faPlug,
  faChartBar,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faHandshake,
  faRocket,
  faCheckCircle,
  faSpinner,
  faSave,
  faPlus,
  faEye,
  faLink,
  faBell,
  faCalendar,
  faFileContract,
  faUserTie,
  faNetworkWired,
  faTools,
  faClipboardList,
  faLightbulb,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAtom } from 'jotai';
import { user_info } from 'jotai/UserJotaiInstance';
import axiosInstance from 'helpers/api';
import { toast } from 'react-hot-toast';
import { UserInfo } from 'interfaces/user';

interface ProfessionalContact {
  id: string;
  name: string;
  company: string;
  email: string;
  phone?: string;
  type: string;
  contact_type?: string; // For API compatibility
  specialization?: string;
  referralPotential: 'low' | 'medium' | 'high';
  referral_potential?: 'low' | 'medium' | 'high'; // For API compatibility
  lastContact?: string;
  notes?: string;
}

interface BusinessSettings {
  companyName: string;
  businessType: string;
  licenseNumber: string;
  brokerageName: string;
  officeAddress: string;
  officePhone: string;
  website: string;
  yearsInBusiness: number;
  specializations: string[];
  serviceAreas: string[];
  businessHours: {
    monday: { open: string; close: string; };
    tuesday: { open: string; close: string; };
    wednesday: { open: string; close: string; };
    thursday: { open: string; close: string; };
    friday: { open: string; close: string; };
    saturday: { open: string; close: string; };
    sunday: { open: string; close: string; };
  };
}

interface Integration {
  id: string;
  name: string;
  type: 'email' | 'calendar' | 'crm' | 'marketing' | 'financial' | 'mls' | 'social';
  status: 'connected' | 'disconnected' | 'error';
  lastSync?: string;
  description: string;
  icon: any;
}

const ProfessionalSettings: React.FC = () => {
  const [userInfo] = useAtom<UserInfo>(user_info);
  const [activeTab, setActiveTab] = useState('business-profile');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [professionalComplete, setProfessionalComplete] = useState(false);
  
  // Add authentication debugging
  const [authStatus, setAuthStatus] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking');
  
  // Business Profile State
  const [businessSettings, setBusinessSettings] = useState<BusinessSettings>({
    companyName: '',
    businessType: 'Real Estate Agent',
    licenseNumber: '',
    brokerageName: '',
    officeAddress: '',
    officePhone: '',
    website: '',
    yearsInBusiness: 0,
    specializations: [],
    serviceAreas: [],
    businessHours: {
      monday: { open: '09:00', close: '17:00' },
      tuesday: { open: '09:00', close: '17:00' },
      wednesday: { open: '09:00', close: '17:00' },
      thursday: { open: '09:00', close: '17:00' },
      friday: { open: '09:00', close: '17:00' },
      saturday: { open: '10:00', close: '16:00' },
      sunday: { open: 'closed', close: 'closed' }
    }
  });

  // Professional Contacts State
  const [professionalContacts, setProfessionalContacts] = useState<ProfessionalContact[]>([]);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState<Partial<ProfessionalContact>>({});

  // Integrations State
  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: 'gmail',
      name: 'Gmail',
      type: 'email',
      status: 'disconnected',
      description: 'Connect your Gmail for email automation',
      icon: faEnvelope
    },
    {
      id: 'google-calendar',
      name: 'Google Calendar',
      type: 'calendar',
      status: 'disconnected',
      description: 'Sync appointments and showings',
      icon: faCalendar
    },
    {
      id: 'facebook-ads',
      name: 'Facebook Ads',
      type: 'marketing',
      status: 'disconnected',
      description: 'Import leads from Facebook advertising',
      icon: faNetworkWired
    },
    {
      id: 'zillow',
      name: 'Zillow',
      type: 'crm',
      status: 'disconnected',
      description: 'Import leads from Zillow Premier Agent',
      icon: faBuilding
    },
    {
      id: 'quickbooks',
      name: 'QuickBooks',
      type: 'financial',
      status: 'disconnected',
      description: 'Sync financial data and commission tracking',
      icon: faFileContract
    },
    {
      id: 'armls',
      name: 'ARMLS',
      type: 'mls',
      status: 'connected',
      lastSync: '2025-06-23T16:35:00Z',
      description: 'Arizona Regional MLS integration',
      icon: faHandshake
    }
  ]);

  // Professional Metrics State
  const [professionalMetrics, setProfessionalMetrics] = useState({
    totalContacts: 0,
    activeReferrals: 0,
    monthlyLeads: 0,
    conversionRate: 0,
    averageResponseTime: 0,
    professionalNetworkScore: 0
  });

  const businessTypes = [
    'Real Estate Agent',
    'Real Estate Broker',
    'Team Leader',
    'Brokerage Owner',
    'Property Manager',
    'Real Estate Investor',
    'Commercial Agent',
    'Residential Specialist'
  ];

  const specializationOptions = [
    'First-Time Buyers',
    'Luxury Properties',
    'Investment Properties',
    'Commercial Real Estate',
    'New Construction',
    'Foreclosures/REO',
    'Relocation Services',
    'Senior Housing',
    'Vacation Homes',
    'Land/Acreage'
  ];

  const professionalContactTypes = [
    'Lender/Mortgage Broker',
    'Title Company',
    'Home Inspector',
    'Contractor',
    'Real Estate Attorney',
    'Insurance Agent',
    'Appraiser',
    'Photographer',
    'Stager',
    'Accountant/CPA',
    'Other'
  ];

  // Professional contact type mapping for API
  const contactTypeMapping = {
    'Lender/Mortgage Broker': 'lender',
    'Title Company': 'title_company',
    'Home Inspector': 'home_inspector',
    'Contractor': 'contractor',
    'Real Estate Attorney': 'attorney',
    'Insurance Agent': 'insurance_agent',
    'Appraiser': 'appraiser',
    'Photographer': 'photographer',
    'Stager': 'stager',
    'Accountant/CPA': 'accountant',
    'Other': 'other'
  };

  // Reverse mapping for display
  const displayTypeMapping = {
    'lender': 'Lender/Mortgage Broker',
    'mortgage_broker': 'Lender/Mortgage Broker',
    'title_company': 'Title Company',
    'home_inspector': 'Home Inspector',
    'contractor': 'Contractor',
    'attorney': 'Real Estate Attorney',
    'insurance_agent': 'Insurance Agent',
    'appraiser': 'Appraiser',
    'photographer': 'Photographer',
    'stager': 'Stager',
    'accountant': 'Accountant/CPA',
    'other': 'Other'
  };

  // Check authentication status on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userInfoStr = localStorage.getItem('user_info');
    
    console.log('🔐 [PROFESSIONAL SETTINGS] Auth check:', {
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : null,
      hasUserInfo: !!userInfoStr,
      userInfo: userInfoStr ? JSON.parse(userInfoStr) : null,
      jotaiUserInfo: userInfo
    });
    
    if (token) {
      setAuthStatus('authenticated');
    } else {
      setAuthStatus('unauthenticated');
    }
  }, [userInfo]);

  // Load professional data function - simplified to avoid dependency issues
  const loadProfessionalData = async () => {
    if (!userInfo?.id) {
      console.log('❌ [PROFESSIONAL SETTINGS] No user ID available');
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 [PROFESSIONAL SETTINGS] Loading professional data...');

      // Load business settings from onboarding status
      console.log('🔍 [PROFESSIONAL SETTINGS] Making API call to:', `onboarding/status?userId=${userInfo.id}`);
      const statusResponse = await axiosInstance.get(`onboarding/status?userId=${userInfo.id}`);
      console.log('📥 [PROFESSIONAL SETTINGS] Full API response:', statusResponse.data);
      
      if (statusResponse.data.success && statusResponse.data.data?.agentInfo) {
        const agentInfo = statusResponse.data.data.agentInfo;
        console.log('✅ [PROFESSIONAL SETTINGS] Agent info loaded:', agentInfo);

        // Create new business settings object
        const newBusinessSettings = {
          companyName: agentInfo.officeName || agentInfo.companyName || '',
          licenseNumber: agentInfo.licensNumber || agentInfo.licenseNumber || '',
          brokerageName: agentInfo.officeName || agentInfo.brokerageName || '',
          businessType: agentInfo.businessType || 'Real Estate Agent',
          officeAddress: agentInfo.officeAddress || '',
          officePhone: agentInfo.officePhone || '',
          website: agentInfo.website || agentInfo.agentWebsite || '',
          yearsInBusiness: agentInfo.yearsInBusiness || 0,
          specializations: agentInfo.specializations || [],
          serviceAreas: agentInfo.serviceAreas || [],
          businessHours: agentInfo.businessHours || {
            monday: { open: '09:00', close: '17:00' },
            tuesday: { open: '09:00', close: '17:00' },
            wednesday: { open: '09:00', close: '17:00' },
            thursday: { open: '09:00', close: '17:00' },
            friday: { open: '09:00', close: '17:00' },
            saturday: { open: '10:00', close: '16:00' },
            sunday: { open: 'closed', close: 'closed' }
          }
        };

        console.log('📋 [PROFESSIONAL SETTINGS] Setting business settings to:', newBusinessSettings);
        setBusinessSettings(newBusinessSettings);
        
        setProfessionalComplete(statusResponse.data.data.completionPercentage >= 80);
      } else {
        console.log('⚠️ [PROFESSIONAL SETTINGS] No agent info found in response:', statusResponse.data);
      }

      // Load professional contacts
      const contactsResponse = await axiosInstance.get('professional-contacts');
      if (contactsResponse.data.success) {
        console.log('✅ [PROFESSIONAL SETTINGS] Professional contacts loaded:', contactsResponse.data.data);
        setProfessionalContacts(contactsResponse.data.data);
      }

      // Load professional metrics
      const metricsResponse = await axiosInstance.get('professional-contacts/metrics');
      if (metricsResponse.data.success) {
        console.log('✅ [PROFESSIONAL SETTINGS] Professional metrics loaded:', metricsResponse.data.data);
        setProfessionalMetrics(metricsResponse.data.data);
      }

    } catch (error: any) {
      console.error('❌ [PROFESSIONAL SETTINGS] Error loading professional data:', error);
      toast.error('Failed to load professional data');
    } finally {
      setLoading(false);
    }
  };

  // Load professional data on component mount
  useEffect(() => {
    console.log('🚀 [PROFESSIONAL SETTINGS] Component mounted/updated');
    
    // Check authentication status
    const token = localStorage.getItem('auth_token');
    console.log('🔐 [PROFESSIONAL SETTINGS] Auth token available:', !!token);
    if (token) {
      console.log('🔐 [PROFESSIONAL SETTINGS] Token preview:', token.substring(0, 20) + '...');
    }
    
    // Check user info state
    console.log('👤 [PROFESSIONAL SETTINGS] User info state:', userInfo);
    console.log('👤 [PROFESSIONAL SETTINGS] User ID:', userInfo?.id);
    
    if (userInfo?.id) {
      console.log('✅ [PROFESSIONAL SETTINGS] User ID found, calling loadProfessionalData');
      loadProfessionalData();
    } else {
      console.log('❌ [PROFESSIONAL SETTINGS] No user ID found, skipping data load');
    }
  }, [userInfo, userInfo?.id]); // Fix: Watch userInfo object AND userInfo?.id

  // Add a simple test function to manually trigger data loading
  const testDataLoad = async () => {
    console.log('🧪 [TEST] Manual data load triggered');
    console.log('🧪 [TEST] User ID:', userInfo?.id);
    if (userInfo?.id) {
      await loadProfessionalData();
    } else {
      console.log('🧪 [TEST] No user ID available');
    }
  };

  const getCompletionPercentage = useCallback(() => {
    const fields = [
      businessSettings.companyName,
      businessSettings.licenseNumber,
      businessSettings.brokerageName,
      businessSettings.officeAddress,
      businessSettings.website,
      businessSettings.specializations.length > 0,
      businessSettings.serviceAreas.length > 0,
      professionalContacts.length > 0
    ];
    
    const completedFields = fields.filter(field => field).length;
    const percentage = Math.round((completedFields / fields.length) * 100);
    
    // If we have MLS data pre-populated, we're already at a good completion level
    if (businessSettings.companyName && businessSettings.licenseNumber && businessSettings.officeAddress) {
      return Math.max(percentage, 75); // Minimum 75% if core MLS data is present
    }
    
    return percentage;
  }, [businessSettings, professionalContacts]);

  const saveBusinessSettings = useCallback(async () => {
    setSaving(true);
    try {
      // Save business settings to database
      console.log('💾 [PROFESSIONAL SETTINGS] Saving business settings:', businessSettings);
      
      const response = await axiosInstance.post('onboarding/professional-settings', {
        businessSettings,
        userId: userInfo?.id
      });
      
      console.log('✅ [PROFESSIONAL SETTINGS] Business settings saved:', response.data);
      toast.success('Business settings saved successfully!');
      
      // Check if profile is complete enough to mark as done
      if (getCompletionPercentage() >= 80) {
        setProfessionalComplete(true);
        // Save completion status to localStorage for parent component
        localStorage.setItem('businessProfileComplete', 'true');
        
        // Show success message with navigation option
        toast.success('🎉 Business profile completed! Ready to build your professional network.', {
          duration: 4000
        });
        
        // Auto-navigate to Professional Network tab after a short delay
        setTimeout(() => {
          setActiveTab('professional-network');
        }, 2000);
      }
    } catch (error: any) {
      console.error('❌ [PROFESSIONAL SETTINGS] Failed to save business settings:', error);
      if (error.response?.data?.message) {
        toast.error(`Failed to save: ${error.response.data.message}`);
      } else {
        toast.error('Failed to save business settings');
      }
    } finally {
      setSaving(false);
    }
  }, [businessSettings, userInfo?.id, getCompletionPercentage]);

  const addProfessionalContact = useCallback(async () => {
    if (!newContact.name || !newContact.company || !newContact.type) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setSaving(true);
      console.log('➕ [PROFESSIONAL CONTACTS] Creating contact:', newContact);

      // Map the display type to API type
      const apiContactType = contactTypeMapping[newContact.type as keyof typeof contactTypeMapping];
      
      const contactData = {
        name: newContact.name,
        company: newContact.company,
        email: newContact.email || '',
        phone: newContact.phone || '',
        contact_type: apiContactType,
        specialization: newContact.specialization || '',
        referral_potential: newContact.referralPotential || 'medium',
        notes: newContact.notes || ''
      };

      const response = await axiosInstance.post('professional-contacts', contactData);
      
      if (response.data.success) {
        console.log('✅ [PROFESSIONAL CONTACTS] Contact created successfully');
        
        // Add to local state
        setProfessionalContacts(prev => [...prev, response.data.data]);
        
        // Reset form
        setNewContact({});
        setShowAddContact(false);
        
        // Reload metrics
        loadProfessionalData();
        
        toast.success('Professional contact added successfully!');
      }
    } catch (error: any) {
      console.error('❌ [PROFESSIONAL CONTACTS] Error creating contact:', error);
      toast.error('Failed to add professional contact');
    } finally {
      setSaving(false);
    }
  }, [newContact, contactTypeMapping, loadProfessionalData]);

  const connectIntegration = useCallback(async (integrationId: string) => {
    try {
      // TODO: Replace with real integration logic
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate connection
      
      setIntegrations(prev => prev.map(integration => 
        integration.id === integrationId 
          ? { ...integration, status: 'connected', lastSync: new Date().toISOString() }
          : integration
      ));
      toast.success('Integration connected successfully!');
    } catch (error: any) {
      toast.error('Failed to connect integration');
    }
  }, []);

  const getIntegrationStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'error':
        return 'danger';
      default:
        return 'secondary';
    }
  }, []);

  const tabConfig = [
    {
      key: 'business-profile',
      title: 'Business Profile',
      icon: faBuilding,
      description: 'Company information and business details'
    },
    {
      key: 'professional-network',
      title: 'Professional Network',
      icon: faUsers,
      description: 'Manage your professional contacts and referral partners'
    },
    {
      key: 'integrations',
      title: 'Integrations',
      icon: faPlug,
      description: 'Connect third-party tools and services'
    },
    {
      key: 'automation',
      title: 'Automation',
      icon: faRocket,
      description: 'Configure automated workflows and processes'
    }
  ];

  // Tab components - NOT memoized to prevent input focus issues
  const BusinessProfileTab = (
    <div>
      {/* Professional Complete Success State */}
      {professionalComplete && (
        <Alert variant="success" className="d-flex align-items-center mb-4">
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          <div className="flex-grow-1">
            <strong>Professional Profile Complete!</strong>
            <div className="small mt-1">
              Your business profile is set up and ready to attract leads and build professional relationships.
            </div>
          </div>
          <Button 
            variant="outline-success" 
            size="sm"
            onClick={() => {
              // Navigate to Security tab or Dashboard
              window.location.href = '/apps/crm/agent-analytics';
            }}
          >
            <FontAwesomeIcon icon={faRocket} className="me-1" />
            View Dashboard
          </Button>
        </Alert>
      )}

      {/* Profile Completion Status */}
      <Alert 
        variant={getCompletionPercentage() === 100 ? 'success' : 'info'} 
        className="d-flex align-items-center mb-4"
      >
        <FontAwesomeIcon 
          icon={getCompletionPercentage() === 100 ? faCheckCircle : faInfoCircle} 
          className="me-2" 
        />
        <div className="flex-grow-1">
          <strong>Profile Completion: {getCompletionPercentage()}%</strong>
          <div className="small mt-1">
            {getCompletionPercentage() === 100 
              ? 'Your professional profile is complete!'
              : getCompletionPercentage() >= 75
              ? 'Great start! Your MLS data has been pre-populated. Add specializations and service areas to complete your profile.'
              : 'Complete your profile to maximize lead generation and professional networking'
            }
          </div>
        </div>
        {/* Debug button to test data loading */}
        <Button 
          variant="outline-info" 
          size="sm"
          onClick={() => {
            console.log('🔧 [DEBUG] Manual data reload triggered');
            loadProfessionalData();
          }}
        >
          <FontAwesomeIcon icon={faRocket} className="me-1" />
          Debug: Reload Data
        </Button>
      </Alert>

      {/* MLS Data Pre-population Notice */}
      {businessSettings.companyName && businessSettings.licenseNumber && (
        <Alert variant="success" className="mb-4 d-flex align-items-center">
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          <div>
            <strong>MLS Data Loaded!</strong>
            <div className="small mt-1">
              Your business information has been automatically populated from your verified MLS profile. 
              You can edit any details below.
            </div>
          </div>
        </Alert>
      )}

      <Row className="g-4">
        {/* Company Information */}
        <Col lg={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <FontAwesomeIcon icon={faBuilding} className="me-2" />
              Company Information
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Company/Team Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={businessSettings.companyName}
                  onChange={(e) => setBusinessSettings(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder="Your real estate business name"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Business Type</Form.Label>
                <Form.Select
                  value={businessSettings.businessType}
                  onChange={(e) => setBusinessSettings(prev => ({ ...prev, businessType: e.target.value }))}
                >
                  {businessTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>License Number</Form.Label>
                <Form.Control
                  type="text"
                  value={businessSettings.licenseNumber}
                  onChange={(e) => setBusinessSettings(prev => ({ ...prev, licenseNumber: e.target.value }))}
                  placeholder="Your real estate license number"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Brokerage Name</Form.Label>
                <Form.Control
                  type="text"
                  value={businessSettings.brokerageName}
                  onChange={(e) => setBusinessSettings(prev => ({ ...prev, brokerageName: e.target.value }))}
                  placeholder="Your brokerage or company name"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Years in Business</Form.Label>
                <Form.Control
                  type="number"
                  value={businessSettings.yearsInBusiness}
                  onChange={(e) => setBusinessSettings(prev => ({ ...prev, yearsInBusiness: parseInt(e.target.value) || 0 }))}
                  min="0"
                  max="50"
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        {/* Contact Information */}
        <Col lg={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-info text-white">
              <FontAwesomeIcon icon={faPhone} className="me-2" />
              Contact Information
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Office Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={businessSettings.officeAddress}
                  onChange={(e) => setBusinessSettings(prev => ({ ...prev, officeAddress: e.target.value }))}
                  placeholder="Your business address"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Office Phone</Form.Label>
                <Form.Control
                  type="text"
                  value={businessSettings.officePhone}
                  onChange={(e) => setBusinessSettings(prev => ({ ...prev, officePhone: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  type="url"
                  value={businessSettings.website}
                  onChange={(e) => setBusinessSettings(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://yourwebsite.com"
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        {/* Specializations */}
        <Col lg={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-success text-white">
              <FontAwesomeIcon icon={faUserTie} className="me-2" />
              Specializations
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                Select your areas of expertise to attract the right clients
              </p>
              {specializationOptions.map(spec => (
                <Form.Check
                  key={spec}
                  type="checkbox"
                  id={`spec-${spec}`}
                  label={spec}
                  checked={businessSettings.specializations.includes(spec)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setBusinessSettings(prev => ({
                        ...prev,
                        specializations: [...prev.specializations, spec]
                      }));
                    } else {
                      setBusinessSettings(prev => ({
                        ...prev,
                        specializations: prev.specializations.filter(s => s !== spec)
                      }));
                    }
                  }}
                  className="mb-2"
                />
              ))}
            </Card.Body>
          </Card>
        </Col>

        {/* Service Areas */}
        <Col lg={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-warning text-dark">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
              Service Areas
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Geographic Areas You Serve</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={businessSettings.serviceAreas.join('\n')}
                  onChange={(e) => setBusinessSettings(prev => ({
                    ...prev,
                    serviceAreas: e.target.value.split('\n').filter(area => area.trim())
                  }))}
                  placeholder="Enter each area on a new line:&#10;Phoenix, AZ&#10;Scottsdale, AZ&#10;Tempe, AZ"
                />
              </Form.Group>
              <small className="text-muted">
                Enter each service area on a new line. This helps with lead routing and local SEO.
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          {getCompletionPercentage() >= 80 && !professionalComplete && (
            <small className="text-muted">
              <FontAwesomeIcon icon={faLightbulb} className="me-1" />
              Your profile is looking great! Save to complete this section.
            </small>
          )}
          {professionalComplete && (
            <small className="text-success">
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              Business profile complete! Ready to build your professional network.
            </small>
          )}
        </div>
        <div className="d-flex gap-2">
          {professionalComplete && (
            <Button 
              variant="outline-primary" 
              size="lg"
              onClick={() => setActiveTab('professional-network')}
              className="px-4"
            >
              <FontAwesomeIcon icon={faUsers} className="me-2" />
              Continue to Professional Network
            </Button>
          )}
          <Button 
            variant="primary" 
            size="lg"
            onClick={saveBusinessSettings}
            disabled={saving}
            className="px-4"
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {professionalComplete ? 'Update Profile' : 'Save & Complete'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  const ProfessionalNetworkTab = (
    <div>
      {/* Network Overview */}
      <Row className="g-4 mb-4">
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <FontAwesomeIcon icon={faUsers} size="2x" className="text-primary mb-2" />
              <h4 className="mb-0">{professionalContacts.length}</h4>
              <small className="text-muted">Professional Contacts</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <FontAwesomeIcon icon={faHandshake} size="2x" className="text-success mb-2" />
              <h4 className="mb-0">{professionalMetrics.activeReferrals}</h4>
              <small className="text-muted">Active Referrals</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <FontAwesomeIcon icon={faChartBar} size="2x" className="text-info mb-2" />
              <h4 className="mb-0">{professionalMetrics.monthlyLeads}</h4>
              <small className="text-muted">Monthly Referral Leads</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <FontAwesomeIcon icon={faNetworkWired} size="2x" className="text-warning mb-2" />
              <h4 className="mb-0">{professionalMetrics.professionalNetworkScore}</h4>
              <small className="text-muted">Network Score</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add New Contact */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Professional Contacts
          </div>
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => setShowAddContact(!showAddContact)}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add Contact
          </Button>
        </Card.Header>

        {showAddContact && (
          <Card.Body className="border-bottom">
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newContact.name || ''}
                    onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Contact full name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Company *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newContact.company || ''}
                    onChange={(e) => setNewContact(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Company name"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Type *</Form.Label>
                  <Form.Select
                    value={newContact.type || ''}
                    onChange={(e) => setNewContact(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="">Select type...</option>
                    {professionalContactTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={newContact.email || ''}
                    onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@company.com"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={newContact.phone || ''}
                    onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <div className="d-flex justify-content-end gap-2">
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setShowAddContact(false)}
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={addProfessionalContact}>
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Add Contact
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        )}

        <Card.Body>
          {professionalContacts.length === 0 ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faUsers} size="3x" className="text-muted mb-3" />
              <h5>No Professional Contacts Yet</h5>
              <p className="text-muted">
                Build your professional network by adding lenders, title companies, inspectors, and other service providers.
              </p>
            </div>
          ) : (
            <Row className="g-3">
              {professionalContacts.map(contact => (
                <Col md={6} key={contact.id}>
                  <Card className="border h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-1">{contact.name}</h6>
                          <small className="text-muted">{contact.company}</small>
                        </div>
                        <Badge bg={contact.referralPotential === 'high' ? 'success' : contact.referralPotential === 'medium' ? 'warning' : 'secondary'}>
                          {contact.referralPotential} referral
                        </Badge>
                      </div>
                      <div className="mb-2">
                        <Badge bg="outline-primary">
                          {displayTypeMapping[contact.type as keyof typeof displayTypeMapping] || contact.type}
                        </Badge>
                        {contact.specialization && (
                          <Badge bg="outline-primary">
                            {contact.specialization}
                          </Badge>
                        )}
                      </div>
                      {contact.email && (
                        <div className="small text-muted mb-1">
                          <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                          {contact.email}
                        </div>
                      )}
                      {contact.phone && (
                        <div className="small text-muted">
                          <FontAwesomeIcon icon={faPhone} className="me-1" />
                          {contact.phone}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>
    </div>
  );

  const IntegrationsTab = useCallback(() => (
    <div>
      <Alert variant="info" className="mb-4">
        <FontAwesomeIcon icon={faLightbulb} className="me-2" />
        <strong>Boost Your Productivity:</strong> Connect your favorite tools to automate lead management, 
        sync calendars, and streamline your workflow.
      </Alert>

      <Row className="g-4">
        {integrations.map(integration => (
          <Col md={6} lg={4} key={integration.id}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center">
                <FontAwesomeIcon 
                  icon={integration.icon} 
                  size="3x" 
                  className={`mb-3 text-${getIntegrationStatusColor(integration.status) === 'success' ? 'success' : 'muted'}`} 
                />
                <h5 className="mb-2">{integration.name}</h5>
                <p className="text-muted small mb-3">{integration.description}</p>
                
                <Badge 
                  bg={getIntegrationStatusColor(integration.status)} 
                  className="mb-3"
                >
                  {integration.status === 'connected' ? 'Connected' : 
                   integration.status === 'error' ? 'Error' : 'Not Connected'}
                </Badge>

                {integration.lastSync && (
                  <div className="small text-muted mb-3">
                    Last sync: {new Date(integration.lastSync).toLocaleDateString()}
                  </div>
                )}

                <div className="d-grid">
                  {integration.status === 'connected' ? (
                    <>
                      <Button variant="outline-success" size="sm" className="mb-2">
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        View Settings
                      </Button>
                      <Button variant="outline-danger" size="sm">
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => connectIntegration(integration.id)}
                    >
                      <FontAwesomeIcon icon={faLink} className="me-1" />
                      Connect
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Integration Categories */}
      <Card className="mt-4 border-0 shadow-sm">
        <Card.Header>
          <FontAwesomeIcon icon={faTools} className="me-2" />
          Available Integration Categories
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <h6><FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />Email & Communication</h6>
              <ul className="list-unstyled small text-muted">
                <li>• Gmail, Outlook email automation</li>
                <li>• SMS/Text messaging platforms</li>
                <li>• Video conferencing tools</li>
              </ul>
            </Col>
            <Col md={6}>
              <h6><FontAwesomeIcon icon={faCalendar} className="me-2 text-success" />Calendar & Scheduling</h6>
              <ul className="list-unstyled small text-muted">
                <li>• Google Calendar, Outlook Calendar</li>
                <li>• Calendly, Acuity Scheduling</li>
                <li>• Showing Time, ShowingDesk</li>
              </ul>
            </Col>
            <Col md={6}>
              <h6><FontAwesomeIcon icon={faNetworkWired} className="me-2 text-info" />Lead Generation</h6>
              <ul className="list-unstyled small text-muted">
                <li>• Zillow Premier Agent</li>
                <li>• Facebook & Google Ads</li>
                <li>• Realtor.com, Trulia leads</li>
              </ul>
            </Col>
            <Col md={6}>
              <h6><FontAwesomeIcon icon={faFileContract} className="me-2 text-warning" />Financial & Legal</h6>
              <ul className="list-unstyled small text-muted">
                <li>• QuickBooks, FreshBooks</li>
                <li>• DocuSign, HelloSign</li>
                <li>• Commission tracking tools</li>
              </ul>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  ), [integrations, getIntegrationStatusColor, connectIntegration]);

  const AutomationTab = useCallback(() => (
    <div>
      <Alert variant="success" className="mb-4">
        <FontAwesomeIcon icon={faRocket} className="me-2" />
        <strong>Automation Active:</strong> Your KairosIQ system is automatically managing lead workflows, 
        follow-ups, and professional contact routing.
      </Alert>

      <Row className="g-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <FontAwesomeIcon icon={faClipboardList} className="me-2" />
              Lead Automation
            </Card.Header>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span>Auto-assign lead workflows</span>
                <Badge bg="success">Active</Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span>Professional contact classification</span>
                <Badge bg="success">Active</Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span>Response time tracking</span>
                <Badge bg="success">Active</Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span>Lead scoring & prioritization</span>
                <Badge bg="success">Active</Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-info text-white">
              <FontAwesomeIcon icon={faBell} className="me-2" />
              Notification Settings
            </Card.Header>
            <Card.Body>
              <Form.Check
                type="switch"
                id="email-notifications"
                label="Email notifications for new leads"
                defaultChecked
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="sms-notifications"
                label="SMS alerts for high-priority leads"
                defaultChecked
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="follow-up-reminders"
                label="Automated follow-up reminders"
                defaultChecked
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="professional-referrals"
                label="Professional referral notifications"
                defaultChecked
              />
            </Card.Body>
          </Card>
        </Col>

        <Col md={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-success text-white">
              <FontAwesomeIcon icon={faChartBar} className="me-2" />
              Automation Performance
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                <Col md={3} className="text-center">
                  <h4 className="text-success">{professionalMetrics.averageResponseTime}min</h4>
                  <small className="text-muted">Average Response Time</small>
                </Col>
                <Col md={3} className="text-center">
                  <h4 className="text-info">{professionalMetrics.conversionRate}%</h4>
                  <small className="text-muted">Lead Conversion Rate</small>
                </Col>
                <Col md={3} className="text-center">
                  <h4 className="text-warning">{professionalContacts.filter(c => c.referralPotential === 'high').length}</h4>
                  <small className="text-muted">High-Value Contacts</small>
                </Col>
                <Col md={3} className="text-center">
                  <h4 className="text-primary">98%</h4>
                  <small className="text-muted">Automation Uptime</small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  ), [professionalMetrics, professionalContacts]);

  if (authStatus === 'checking') {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-3" />
        <p>Checking authentication...</p>
      </div>
    );
  }

  if (authStatus === 'unauthenticated') {
    return (
      <Alert variant="warning" className="text-center">
        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
        <strong>Authentication Required</strong>
        <p className="mb-0 mt-2">Please log in to access professional settings.</p>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-3" />
        <p>Loading professional settings...</p>
      </div>
    );
  }

  return (
    <div className="professional-settings">
      {/* Debug Section - Remove after testing */}
      <Alert variant="info" className="mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong>Debug Info:</strong> User ID: {userInfo?.id || 'Not available'} | 
            Company: {businessSettings.companyName || 'Empty'} | 
            License: {businessSettings.licenseNumber || 'Empty'}
          </div>
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={testDataLoad}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Test Load Data'}
          </Button>
        </div>
      </Alert>

      {/* Tab Navigation */}
      <Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'business-profile')}>
        <Nav variant="pills" className="nav-pills-enhanced mb-4">
          {tabConfig.map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link eventKey={tab.key} className="d-flex align-items-center">
                <FontAwesomeIcon icon={tab.icon} className="me-2" />
                <div className="text-start">
                  <div className="fw-semibold">{tab.title}</div>
                  <small className="text-muted d-none d-lg-block">{tab.description}</small>
                </div>
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        {/* Tab Content */}
        <Tab.Content>
          <Tab.Pane eventKey="business-profile">
            {BusinessProfileTab}
          </Tab.Pane>
          <Tab.Pane eventKey="professional-network">
            {ProfessionalNetworkTab}
          </Tab.Pane>
          <Tab.Pane eventKey="integrations">
            <IntegrationsTab />
          </Tab.Pane>
          <Tab.Pane eventKey="automation">
            <AutomationTab />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default ProfessionalSettings; 